import { create } from 'zustand';
import type { SessionInfo, AgentEvent, Anomaly, AgentNode, Edge } from '../utils/eventSchema';
import { getAgentColor } from '../utils/agentColors';

interface SessionState {
  // Session list
  sessions: Record<string, SessionInfo>;
  activeSessionId: string | null;

  // Graph state
  nodes: Record<string, AgentNode>;
  edges: Record<string, Edge>;

  // Live event stream
  events: AgentEvent[];

  // Anomaly state
  anomalies: Anomaly[];
  unacknowledgedAlerts: Anomaly[];

  // Connection state
  isConnected: boolean;
  connectionStatus: 'connecting' | 'live' | 'reconnecting' | 'disconnected';

  // Replay state
  isReplaying: boolean;
  replayPosition: number;
  replaySpeed: 1 | 2 | 5;
  replayEvents: AgentEvent[];

  // UI state
  selectedNodeId: string | null;
  selectedEventIndex: number | null;
  detailPanelTab: 'messages' | 'agents' | 'replay';

  // Actions
  setConnected: (v: boolean, status?: SessionState['connectionStatus']) => void;
  addEvent: (event: AgentEvent) => void;
  addAnomaly: (anomaly: Anomaly) => void;
  dismissAlert: (anomalyId: string) => void;
  selectSession: (id: string | null) => void;
  setActiveSession: (session: SessionInfo) => void;
  setReplayPosition: (ms: number) => void;
  setReplaySpeed: (speed: 1 | 2 | 5) => void;
  setIsReplaying: (v: boolean) => void;
  selectNode: (nodeId: string | null) => void;
  selectEvent: (index: number | null) => void;
  setDetailPanelTab: (tab: SessionState['detailPanelTab']) => void;
  resetSession: () => void;
}

export const useSessionStore = create<SessionState>((set) => ({
  sessions: {},
  activeSessionId: null,
  nodes: {},
  edges: {},
  events: [],
  anomalies: [],
  unacknowledgedAlerts: [],
  isConnected: false,
  connectionStatus: 'disconnected',
  isReplaying: false,
  replayPosition: 0,
  replaySpeed: 1,
  replayEvents: [],
  selectedNodeId: null,
  selectedEventIndex: null,
  detailPanelTab: 'messages',

  setConnected: (v, status) =>
    set({ isConnected: v, connectionStatus: status ?? (v ? 'live' : 'disconnected') }),

  addEvent: (event) => {
    set((state) => {
      const nodes = { ...state.nodes };
      const edges = { ...state.edges };

      const targetId = event.target_agent || event.agent_id;
      if (!nodes[targetId]) {
        nodes[targetId] = {
          id: targetId,
          name: targetId,
          messageCount: 0,
          tokenCount: 0,
          lastActive: event.timestamp_ms || Date.now(),
          color: getAgentColor(targetId),
        };
      }
      nodes[targetId] = {
        ...nodes[targetId],
        messageCount: nodes[targetId].messageCount + 1,
        tokenCount: nodes[targetId].tokenCount + event.token_count,
        lastActive: event.timestamp_ms || Date.now(),
      };

      if (event.source_agent && event.source_agent !== targetId) {
        if (!nodes[event.source_agent]) {
          nodes[event.source_agent] = {
            id: event.source_agent,
            name: event.source_agent,
            messageCount: 0,
            tokenCount: 0,
            lastActive: event.timestamp_ms || Date.now(),
            color: getAgentColor(event.source_agent),
          };
        }
      }

      const sourceId = event.source_agent || 'system';
      const targetKey = `${sourceId}→${targetId}`;
      if (!edges[targetKey]) {
        edges[targetKey] = { source: sourceId, target: targetId, hitCount: 0, lastHitMs: 0, isAnomaly: false };
      }
      edges[targetKey] = {
        ...edges[targetKey],
        hitCount: edges[targetKey].hitCount + 1,
        lastHitMs: event.timestamp_ms || Date.now(),
      };

      return { nodes, edges, events: [...state.events, event] };
    });
  },

  addAnomaly: (anomaly) => {
    set((state) => {
      const edgeKey =
        anomaly.involved_agents.length >= 2
          ? `${anomaly.involved_agents[0]}→${anomaly.involved_agents[1]}`
          : null;

      const edges =
        edgeKey && state.edges[edgeKey]
          ? { ...state.edges, [edgeKey]: { ...state.edges[edgeKey], isAnomaly: true } }
          : state.edges;

      return {
        anomalies: [...state.anomalies, anomaly],
        unacknowledgedAlerts: [...state.unacknowledgedAlerts, anomaly],
        edges,
      };
    });
  },

  dismissAlert: (anomalyId) => {
    set((state) => ({
      unacknowledgedAlerts: state.unacknowledgedAlerts.filter((a) => a.anomaly_id !== anomalyId),
      anomalies: state.anomalies.map((a) =>
        a.anomaly_id === anomalyId ? { ...a, acknowledged: true } : a
      ),
    }));
  },

  selectSession: (id) => set({ activeSessionId: id }),
  setActiveSession: (session) =>
    set((state) => ({
      sessions: { ...state.sessions, [session.trace_id]: session },
      activeSessionId: session.trace_id,
    })),
  setReplayPosition: (ms) => set({ replayPosition: ms }),
  setReplaySpeed: (speed) => set({ replaySpeed: speed }),
  setIsReplaying: (v) => set({ isReplaying: v }),
  selectNode: (nodeId) => set({ selectedNodeId: nodeId, detailPanelTab: 'agents' }),
  selectEvent: (index) => set({ selectedEventIndex: index }),
  setDetailPanelTab: (tab) => set({ detailPanelTab: tab }),
  resetSession: () =>
    set({
      nodes: {},
      edges: {},
      events: [],
      anomalies: [],
      unacknowledgedAlerts: [],
      selectedNodeId: null,
      selectedEventIndex: null,
    }),
}));
